// Walk → swimlane compose-options builder, shared by the MCP `arch_render`
// tool and the server's /api/process endpoints (scene-process-render P3).
//
// The scene composer is zero-dep and draws from `SwimlaneSceneInput` rows
// alone; the walk-derived signals it can't see — real step labels, comment
// rows, note rows, saga aux edges, `go` re-entries, start triggers, step
// modifiers — are resolved here from the engine `WalkResult` and handed over
// as plain data (shaped to `@archlang/scene/process` `ComposeSwimlaneOptions`,
// no scene import needed). Mirrors the committed live view's derivations:
// `labelForStep`, `isCommentRow`, `buildFlowGraph`'s aux-edge pass, and
// `walk.triggers` (SwimlaneView.tsx / flow-graph.ts at HEAD).

import { walkProcess, isAwaitNodeId } from "@archlang/engine";
import type {
  ProcessModel,
  Row,
  SwimlaneLayout,
  WalkerContext,
  WalkResult,
} from "@archlang/engine";
import { humanizeLabel } from "@archlang/scene/arch-model";

/**
 * Resolve which subprocess `do` node ids to expand for a fresh-open walk —
 * shared by the MCP `arch_render` tool and the server's /api/process endpoints
 * (the two carried byte-identical copies before this).
 *
 *   • `expand === "all"` — expand EVERY subprocess `do`, nested ones included.
 *     Computed by fixpoint: each pass walks with the currently-expanded set and
 *     folds that walk's `collapsedGroups` keys (the still-collapsed `do`
 *     nodeIds) into `expanded`, so a nested `do` exposed by opening its parent
 *     is picked up on the next pass. The loop is UNCAPPED because it provably
 *     converges: the walker's emitter recursion guard closes recursive splices
 *     (process-step-emitter.ts:392-395), so `collapsedGroups` is a finite
 *     frontier drawn from the process's finite `do` tree, `expanded` only ever
 *     grows, and the pass it stops growing is the fixed point.
 *   • `expand` = string[] — expand exactly those `do` node ids.
 *   • absent — expand none.
 */
export function resolveExpandedSubprocesses(
  proc: ProcessModel,
  ctx: WalkerContext,
  expand?: "all" | readonly string[],
): Set<string> {
  const expanded = new Set<string>(expand === "all" ? [] : (expand ?? []));
  if (expand === "all") {
    for (;;) {
      const walk = walkProcess(
        proc,
        { branchSelection: new Map(), expandedSubprocesses: expanded },
        ctx,
        { showAllBranches: true },
      );
      const before = expanded.size;
      for (const id of walk.collapsedGroups.keys()) expanded.add(id);
      if (expanded.size === before) return expanded;
    }
  }
  return expanded;
}

export interface SwimlaneExportAuxEdge {
  readonly id: string;
  readonly kind: "drop" | "comp" | "reentry";
  readonly fromRowIndex: number;
  readonly toRowIndex: number;
  readonly comment?: string;
}

export interface SwimlaneExportOptions {
  readonly blockLabel: (row: Row, rowIndex: number) => { primary: string; subtitle?: string };
  readonly isCommentRow: (row: Row, rowIndex: number) => boolean;
  readonly isNoteRow: (row: Row, rowIndex: number) => boolean;
  readonly stepFlags: (row: Row, rowIndex: number) => { compensation?: boolean; dist?: boolean };
  readonly auxEdges: readonly SwimlaneExportAuxEdge[];
  readonly triggerRowIndexes: ReadonlySet<number>;
  readonly waitRowIndexes: ReadonlySet<number>;
  readonly entryStepIndex?: number;
  /** v0.10.2 per-hop latency label by stepIndex (from `perStepLatency`), so
   *  exported SVG/PNG shows the same arrow labels as the live view. */
  readonly stepLatencyLabel?: (stepIndex: number) => string | undefined;
  /** v0.10.2 cumulative-elapsed label by stepIndex (`perStepLatency` cumLabel) —
   *  the sequence-view ruler gutter. */
  readonly stepCumulativeLabel?: (stepIndex: number) => string | undefined;
}

/**
 * Build the composer options for one fresh-open walk + swimlane layout.
 * `stepLatencyLabel` (optional) is computed by the caller — which has the model
 * — via `perStepLatency(walk, buildLatencyEnv(model, proc))`, keyed by stepIndex.
 */
export function swimlaneExportOptions(
  walk: WalkResult,
  layout: SwimlaneLayout,
  stepLatencyLabel?: (stepIndex: number) => string | undefined,
  stepCumulativeLabel?: (stepIndex: number) => string | undefined,
): SwimlaneExportOptions {
  const step = (r: Row) => (r.stepIndex !== null ? walk.byIndex.get(r.stepIndex) : undefined);

  // Matches the live labelForStep: the primary is the raw last path segment
  // (app-shell's `displayInterfaceNameString` only swaps the `(anonymous)`
  // sentinel, which never appears in `step.to` — the walker uses the callee's
  // modulePath there), and the flow subtitle humanizes the caller lane label
  // via `humanizeLabel` exactly like the live lane-label map (`displayLabel`
  // is a re-export of the same function). Pinned by app-shell's
  // geometry-parity test.
  const blockLabel = (r: Row): { primary: string; subtitle?: string } => {
    if (r.stepIndex === null) return { primary: r.terminator ? (r.terminator.reason ?? "") : "" };
    const s = step(r);
    if (!s) return { primary: `#${r.stepIndex + 1}` };
    if (s.reentryTarget) return { primary: `↩ ${s.reentryTarget}` };
    if (s.noteText) return { primary: s.noteText };
    const ifaceName = s.to[s.to.length - 1] ?? s.to.join(".");
    if (layout.variant === "sequence") {
      const args = s.args && s.args.length > 0 ? `(${s.args.join(", ")})` : "";
      const bind = s.bind ? `${s.bind} = ` : "";
      return { primary: `${bind}${ifaceName}${args}` };
    }
    const callerLane = layout.lanes.find((l) => l.id === r.fromLaneId);
    if (!callerLane || r.fromLaneId === r.toLaneId) return { primary: ifaceName };
    return { primary: ifaceName, subtitle: `from ${humanizeLabel(callerLane.label)}` };
  };

  const isCommentRow = (r: Row): boolean => {
    const s = step(r);
    return !!s && s.noteText !== undefined && s.from.length === 0;
  };
  const isNoteRow = (r: Row): boolean => {
    const s = step(r);
    return !!s && s.noteText !== undefined && s.from.length > 0;
  };
  const stepFlags = (r: Row): { compensation?: boolean; dist?: boolean } => {
    const s = step(r);
    return {
      compensation: s?.modifiers.compensation === true || r.comp !== undefined,
      dist: s?.modifiers.dist === true,
    };
  };

  // nodeId → row index: steps via stepIndex, terminators / do-headers via
  // their row's own nodeId (the committed nodeKeyByNodeId + rowByNodeKey pair,
  // collapsed into one map since the composer takes row indexes directly).
  const rowByNodeId = new Map<string, number>();
  const rowByStepIndex = new Map<number, number>();
  layout.rows.forEach((r, i) => {
    if (r.stepIndex !== null) rowByStepIndex.set(r.stepIndex, i);
    if (r.terminator) rowByNodeId.set(r.terminator.nodeId, i);
    if (r.subprocessHeader) rowByNodeId.set(r.subprocessHeader.nodeId, i);
  });
  const stepIndexByNodeId = new Map<string, number>();
  for (const s of walk.steps) stepIndexByNodeId.set(s.nodeId, s.stepIndex);
  const rowOf = (nodeId: string): number | undefined => {
    const si = stepIndexByNodeId.get(nodeId);
    if (si !== undefined) return rowByStepIndex.get(si);
    return rowByNodeId.get(nodeId);
  };

  const auxEdges: SwimlaneExportAuxEdge[] = [];
  const addAux = (fromId: string | undefined, toId: string | undefined, kind: SwimlaneExportAuxEdge["kind"]): void => {
    const from = fromId !== undefined ? rowOf(fromId) : undefined;
    const to = toId !== undefined ? rowOf(toId) : undefined;
    if (from === undefined || to === undefined || from === to) return;
    const comment = kind === "reentry" ? layout.rows[from]?.comment : undefined;
    auxEdges.push({ id: `aux:${kind}:${from}__${to}`, kind, fromRowIndex: from, toRowIndex: to, ...(comment ? { comment } : {}) });
  };
  for (const f of walk.compensation ?? []) {
    for (let i = 0; i + 1 < f.chain.length; i++) addAux(f.chain[i], f.chain[i + 1], "comp");
    for (const d of f.drops) addAux(d.fromNodeId, d.toNodeId, "drop");
  }
  for (const re of walk.reentries ?? []) addAux(re.fromNodeId, re.toNodeId, "reentry");

  const triggerRowIndexes = new Set<number>();
  for (const nodeId of walk.triggers ?? []) {
    const i = rowOf(nodeId);
    if (i !== undefined) triggerRowIndexes.add(i);
  }

  // Mid-flow `await` waits (an await step that is NOT a leading start trigger) —
  // rendered with a timer/clock marker (PROCESS.md §6), distinct from both a
  // plain note and the `▶` start trigger.
  const triggerSet = new Set(walk.triggers ?? []);
  const waitRowIndexes = new Set<number>();
  for (const s of walk.steps) {
    if (triggerSet.has(s.nodeId)) continue;
    if (!isAwaitNodeId(s.nodeId)) continue;
    const i = rowByStepIndex.get(s.stepIndex);
    if (i !== undefined) waitRowIndexes.add(i);
  }

  // First non-comment step = the entry dot (committed isFirstRealStep).
  let entryStepIndex: number | undefined;
  for (const s of walk.steps) {
    if (s.noteText !== undefined && s.from.length === 0) continue;
    entryStepIndex = s.stepIndex;
    break;
  }

  return {
    blockLabel,
    isCommentRow,
    isNoteRow,
    stepFlags,
    auxEdges,
    triggerRowIndexes,
    waitRowIndexes,
    entryStepIndex,
    ...(stepLatencyLabel ? { stepLatencyLabel } : {}),
    ...(stepCumulativeLabel ? { stepCumulativeLabel } : {}),
  };
}
